import React, {useState} from 'react';
import { useNavigate } from 'react-router-dom'
import styled  from 'styled-components';
import { breakpoints } from '@/layouts/breakpoints';
import { Copy, Check, SquareArrowOutUpRight } from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-async-light';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('python', python);

const StyledComp = styled.div`
    padding: clamp(40px, 8vw, 80px) clamp(20px, 5vw, 40px);
    border-bottom: 1px solid rgba(var(--greyr), 0.3);

    .quick-start-content {
      max-width: 1400px;
      margin: 0 auto;

      .section-header {
        text-align: center;
        margin-bottom: clamp(40px, 5vw, 64px);
        padding: 0 16px;

        b {
          font-size: 9px;
          letter-spacing: 1.5px;
          color: var(--grey);
          display: block;
          margin-bottom: 16px;
        }

        h2 {
          font-size: 36px;
          margin-bottom: 16px;
        }

        p {
          color: var(--grey);
          max-width: 700px;
          margin: 0 auto;
          @media (max-width: ${breakpoints.small}) {
            font-size: 12px;
          }
          }
        }

        .quick-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 0;
          border: 1px solid rgba(var(--greyr), 0.3);
          border-radius: 8px;
          overflow: hidden;
          background-color: var(--white);

          @media (max-width: 768px) {
            grid-template-columns: 1fr;
          }

          @media (max-width: 480px) {
            border: none;
            gap: 16px;
            background-color: transparent;
            overflow: visible;
        }

        .step {
          padding: clamp(24px, 4vw, 40px);
          border-right: 1px solid rgba(var(--greyr), 0.3);
          background-color: var(--white);
          display: flex;
          flex-direction: column;

          &.s3{
            border-right: none;
          }

          @media (max-width: 981px){
            &.s2{
              border-right: none;
            }

            &.s3{
              border-right: 1px solid rgba(var(--greyr), 0.3);
              border-top: 1px solid rgba(var(--greyr), 0.3);
            }
          }

          @media (max-width: 768px) {
            border-right: none;
            border-bottom: 1px solid rgba(var(--greyr), 0.3);
            &.s3{
              border-right: none;
              border-top: none;
              border-bottom: none;
            }

          }

          .step-number {
            font-size: clamp(36px, 5vw, 48px);
            font-family: var(--serif);
            color: var(--grey);
            margin-bottom: 24px;
          }

          h3 {
            font-size: clamp(16px, 2vw, 18px);
            letter-spacing: 0.5px;
            margin-bottom: 16px;
          }

          p {
            font-size: clamp(13px, 1.5vw, 14px);
            line-height: 1.8;
            color: var(--grey);
            margin-bottom: 24px;
          }

          .code-snippet {
            border-radius: 6px;
            font-size: clamp(11px, 1.5vw, 13px);
            color: var(--white);
            margin-top: auto;
            position: relative;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;

            .copy-button {
              position: absolute;
              top: 12px;
              right: 12px;
              background: var(--grey);
              border: 1px solid rgba(255, 255, 255, 0.2);
              border-radius: 8px;
              padding: 6px;
              cursor: pointer;
              transition: all 0.2s;
              min-width: 32px;
              min-height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index:3;

              &:hover {
                background: rgba(var(--greyr), 0.9);
              }

              svg {
                width: 14px;
                height: 14px;
                color: var(--white);
              }
            }
          }
        }
      }
    }

`;

const Comp = ({}) => {
  const navigate = useNavigate()
  const [copiedStep, setCopiedStep] = useState(null);

  const handleCopy = async (text, stepId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStep(stepId);
      setTimeout(() => setCopiedStep(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const snippets = {
    apiKey: 'AXION_API_KEY=axn_...',
    install: 'pip install axionquant-sdk',
    firstCall: `from axion import Axion
client = Axion("AXION_API_KEY")
quote = client.get_stock_prices('AAPL')`,
  };

  return (
    <StyledComp>
      <div className="quick-start-content">
        <div className="section-header">
          <b className="sans">QUICK START</b>
          <h2 className="serif">Get Started with the Python Financial Data API in 3 Steps</h2>
          <p>From signup to your first stock market data call in under 5 minutes</p>
        </div>
        <div className="quick-steps">
          <div className="step s1">
            <div className="step-number">01</div>
            <h3 className="sans">GET YOUR API KEY</h3>
            <p>Sign up for a free account and instantly receive your API key. No credit card required.</p>
            <div className="code-snippet">
              <button
                className={`copy-button`}
                onClick={() => navigate('/dashboard/api-keys')}
                aria-label="Get Key"
              >
                <SquareArrowOutUpRight size={14} />
              </button>
              <SyntaxHighlighter
                style={oneLight}
                language='bash'
              >
                {snippets.apiKey}
              </SyntaxHighlighter>
            </div>
          </div>
          <div className="step s2">
            <div className="step-number">02</div>
            <h3 className="sans">INSTALL THE PYTHON SDK</h3>
            <p>Install the Python finance library, or grab a TypeScript client, with a single command.</p>
            <div className="code-snippet">
              <button
                className={`copy-button ${copiedStep === 'install' ? 'copied' : ''}`}
                onClick={() => handleCopy(snippets.install, 'install')}
                aria-label="Copy code"
              >
                {copiedStep === 'install' ? <Check size={14} /> : <Copy size={14} />}
              </button>
              <SyntaxHighlighter
                style={oneLight}
                language='bash'
              >
                {snippets.install}
              </SyntaxHighlighter>
            </div>
          </div>
          <div className="step s3">
            <div className="step-number">03</div>
            <h3 className="sans">MAKE YOUR FIRST CALL</h3>
            <p>Fetch decades of daily and hourly stock market data back to 1987 with just a few lines of code.</p>
            <div className="code-snippet">
              <button
                className={`copy-button ${copiedStep === 'firstCall' ? 'copied' : ''}`}
                onClick={() => handleCopy(snippets.firstCall, 'firstCall')}
                aria-label="Copy code"
              >
                {copiedStep === 'firstCall' ? <Check size={14} /> : <Copy size={14} />}
              </button>
              <SyntaxHighlighter
                style={oneLight}
                language='python'
              >
                {snippets.firstCall}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;

